import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { ReplaySubject, forkJoin, take, takeUntil } from 'rxjs';
import { utils } from '../../../helper/utils';
import { AuthPolicies } from '../../../auth/auth-policies';
import { ApprovalFilterForm, WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { MaintenanceOrderStatus, MaintenanceOrderStatusCode } from '../../../models/maintenance-order-status.model';
import { Shop } from '../../../models/shop.model';
import { MasterDataService } from '../../../services/master-data.service';
import { ApprovalWoService } from '../../../services/approval-wo.service';
import { ApprovalMasterData } from '../../../models/approval-wo.model';
import { ToastrService } from '../../../services/toastr-service';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { AssigneeList, KeyValue, RepairMode } from '../../../models/loc.model';

@Component({
  selector: 'app-approval-all-advance-filter',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, AemsMultilineTagInputComponent],
  templateUrl: './approval-all-advance-filter.component.html',
  styleUrl: './approval-all-advance-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class ApprovalAllAdvanceFilterComponent implements OnInit {
  @Input({ required: true }) workOrderFilterForm!: FormGroup<ApprovalFilterForm>;
  @Input() isWorkOrderList: boolean = true;
  @Input() bindMultiSelects: boolean = false;

  modes: RepairMode[] = [];
  statuses: MaintenanceOrderStatus[] = [];
  assigneeList: AssigneeList[] = [];
  shops: Shop[] = [];
  workorderRegex: RegExp = /\b5\d{8}\b/g;
  equipmentNumberRegex: RegExp = /\b[a-zA-Z]{4}\d{6,9}\b/g;
  shopIdRegex: RegExp = /\b[a-zA-Z0-9]{3}\b/g;
  masterData: ApprovalMasterData[] = [];
  regions: KeyValue[] = [];
  clusters: KeyValue[] = [];
  shopCodes: KeyValue[] = [];
  shopCountries: KeyValue[] = [];
  allCountries: KeyValue[] = [];
  allShopCodes: KeyValue[] = [];
  batchSize = 50;
  batchDelay = 10;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private masterDataService: MasterDataService,
    private formBuilder: FormBuilder,
    public authPolicy: AuthPolicies,
    private approvalWOService: ApprovalWoService,
    private toastrService: ToastrService,
    private appTextService: ApplicationtTextService,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.getRegionUserDetails();
    this.subscribeToMasterDataApiCalls();
  }
  getRegionUserDetails() {
    let request = {
      Loc: '',
    };
    this.approvalWOService
      .getClusterShopReferenceDetails(request)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          this.masterData = response;
          this.initializeDropdowns();
        },
        error: (err) => {
          this.masterData = [];
          this.toastrService.showToastr(this.appTextService.unableToGetFilterDetails, 'error');
        },
      });
  }

  initializeDropdowns() {
    this.regions = this.masterData
      .filter((item, index, self) => self.findIndex((i) => i.loc === item.loc) === index)
      .map((item) => ({ code: item.loc, name: item.loc }))
      .sort((a, b) => a.name.localeCompare(b.name));

    this.clusters = this.masterData
      .filter((item, index, self) => self.findIndex((i) => i.clusterCode == item.clusterCode) === index)
      .map((item) => ({ code: item.clusterCode, name: item.clusterName, label: item.clusterCode, value: item.clusterCode }))
      .sort((a, b) => a.code.localeCompare(b.code));

    const uniqueMapCountry = new Map();
    this.masterData.forEach((item) => {
      if (!uniqueMapCountry.has(item.shopCountry)) {
        uniqueMapCountry.set(item.shopCountry, {
          code: item.shopCountry,
          name: item.shopCountryName,
          label: item.shopCountry,
          value: item.shopCountry,
        });
      }
    });
    this.allCountries = Array.from(uniqueMapCountry.values()).sort((a, b) => a.code.localeCompare(b.code));
    this.loadCountriesInBatches(0);

    const uniqueMapShopCodes = new Map();
    this.masterData.forEach((item) => {
      if (!uniqueMapShopCodes.has(item.shopCode)) {
        uniqueMapShopCodes.set(item.shopCode, {
          code: item.shopCode,
          name: item.shopCode,
          label: item.shopCode,
          value: item.shopCode,
        });
      }
    });
    this.allShopCodes = Array.from(uniqueMapShopCodes.values()).sort((a, b) => a.code.localeCompare(b.code));
    this.loadShopCodesInBatches(0);
  }
  onRegionSelected(event: Event) {
    let customEvent = event as CustomEvent;

    const selectedOptions = customEvent.detail;
    const selectedRegions = selectedOptions.map((opt: { value: any }) => opt.value);

    this.clusters = [];
    const uniqueClusters = new Set<string>();

    this.masterData.forEach((row) => {
      const matchesRegion = selectedRegions.length === 0 || selectedRegions.includes(row.loc);
      if (matchesRegion && !uniqueClusters.has(row.clusterCode)) {
        uniqueClusters.add(row.clusterCode);
        this.clusters.push({
          code: row.clusterCode,
          name: row.clusterCode,
          label: row.clusterCode,
          value: row.clusterCode,
        });
      }
    });
    this.workOrderFilterForm.controls.cluster.setValue(null);
    this.workOrderFilterForm.controls.shopCountry.setValue(null);
    this.workOrderFilterForm.controls.shop.setValue(null);
  }
  onClusterSelected(event: Event) {
    let customEvent = event as CustomEvent;
    const selectedOptions = customEvent.detail;
    const selectedClusters = selectedOptions.map((opt: { value: any }) => opt.value);

    this.shopCountries = [];
    const uniqueCountries = new Set<string>();

    this.masterData.forEach((row) => {
      const matchesCluster = selectedClusters.length === 0 || selectedClusters.includes(row.clusterCode);
      if (matchesCluster && !uniqueCountries.has(row.shopCountry)) {
        uniqueCountries.add(row.shopCountry);
        this.shopCountries.push({
          code: row.shopCountry,
          name: row.shopCountry,
          label: row.shopCountry,
          value: row.shopCountry,
        });
      }
    });
    this.workOrderFilterForm.controls.shopCountry.setValue(null);
    this.workOrderFilterForm.controls.shop.setValue(null);
  }
  onCountrySelected(event: Event) {
    const customEvent = event as CustomEvent;
    const selectedOptions = customEvent.detail;
    const selectedCountries = selectedOptions.map((opt: { value: any }) => opt.value);

    // Prepare for batch loading
    this.shopCodes = [];
    const uniqueShop = new Set<string>();
    const filteredShopCodes: any[] = [];

    // Collect unique shopCodes matching the selected countries
    this.masterData.forEach((row) => {
      const matchesCountry = selectedCountries.length === 0 || selectedCountries.includes(row.shopCountry);
      if (matchesCountry && !uniqueShop.has(row.shopCode)) {
        uniqueShop.add(row.shopCode);
        filteredShopCodes.push({
          code: row.shopCode,
          label: row.shopCode,
          value: row.shopCode,
        });
      }
    });

    // Save the filtered result for batching
    this.allShopCodes = filteredShopCodes;

    // Start batch rendering
    this.loadShopCodesInBatches(0);
    this.workOrderFilterForm.controls.shop.setValue(null);
  }
  loadCountriesInBatches(startIndex: number) {
    const endIndex = startIndex + this.batchSize;
    const batch = this.allCountries.slice(startIndex, endIndex);
    this.shopCountries = this.shopCountries.concat(batch);

    if (endIndex < this.allCountries.length) {
      setTimeout(() => this.loadCountriesInBatches(endIndex), this.batchDelay);
    }
  }
  loadShopCodesInBatches(startIndex: number) {
    const endIndex = startIndex + this.batchSize;
    const batch = this.allShopCodes.slice(startIndex, endIndex);
    this.shopCodes = this.shopCodes.concat(batch);

    if (endIndex < this.allShopCodes.length) {
      setTimeout(() => this.loadShopCodesInBatches(endIndex), this.batchDelay);
    }
  }
  private addControlsToTheFormGroup() {
    this.workOrderFilterForm.addControl('equipmentId', this.formBuilder.control<string[]>([]));
    this.workOrderFilterForm.addControl('mode', this.formBuilder.control<RepairMode[] | null>(null));
    this.workOrderFilterForm.addControl('fromDate', this.formBuilder.control<string | null>(null));
    this.workOrderFilterForm.addControl('toDate', this.formBuilder.control<string | null>(null));
    this.workOrderFilterForm.addControl('woNumber', this.formBuilder.control<string[]>([]));
    this.workOrderFilterForm.addControl('status', this.formBuilder.control<MaintenanceOrderStatus[] | null>(null));
    this.workOrderFilterForm.addControl('shop', this.formBuilder.control<string[] | null>(null));
    this.workOrderFilterForm.addControl('shopCountry', this.formBuilder.control<string[] | null>(null));
    this.workOrderFilterForm.addControl('region', this.formBuilder.control<string[] | null>(null));
    this.workOrderFilterForm.addControl('cluster', this.formBuilder.control<string[] | null>(null));
    this.workOrderFilterForm.addControl('assignedTo', this.formBuilder.control<string | null>(null));
    this.workOrderFilterForm.addControl('isOnHold', this.formBuilder.control<boolean | null>(null));
    this.workOrderFilterForm.addControl('applyWOFilter', this.formBuilder.control<boolean | null>(null));
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  private subscribeToMasterDataApiCalls() {
    forkJoin([
      this.approvalWOService.getRepairModes().pipe(take(1)),
      this.masterDataService.getMaintenanceOrderStatus(),
      this.approvalWOService.getAssigneeList(),
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([modes, status, assigneeList]) => {
        this.modes = modes;
        this.statuses = status;
        this.assigneeList = assigneeList;
        this.setFilterStatusforRole();
      });
  }

  private setFilterStatusforRole() {
    let statusDescription: MaintenanceOrderStatus | undefined;
    if (this.authPolicy.managerRole) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.PendingManagerApproval);
    }
    if (this.authPolicy.specialistRole) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.PendingSpecialistApproval);
    }
    if (this.authPolicy.canMarkWorkOrderAsTTL) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.SuspendForTTLCheck);
    }
    if (this.authPolicy.approverRole) {
      statusDescription = utils.getMaintenanceOrderStatusText(MaintenanceOrderStatusCode.PendingApproval);
    }
    const statues = this.statuses.filter((x) => x.description.toLowerCase() === statusDescription?.description.toLowerCase());
    this.workOrderFilterForm.controls['status'].setValue(statues);
    this.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }
  applyFilters() {
    this.workOrderFilterForm.controls.applyWOFilter.setValue(true);
  }
  clearFilters() {
    this.shopCodes = [];
    this.shopCountries = [];
    this.allCountries = [];
    this.allShopCodes = [];
    this.workOrderFilterForm.reset();
    this.initializeDropdowns();
  }
  onCheckChange(value: boolean) {
    const current = this.workOrderFilterForm.get('isOnHold')?.value;
    if (current === value) {
      this.workOrderFilterForm.get('isOnHold')?.setValue(null);
    } else {
      this.workOrderFilterForm.get('isOnHold')?.setValue(value);
    }
  }
}
